import { DateTime } from 'luxon'
import { fullDaysOfWeek, StatusType, VARIABLE_LIST } from '@constants/Marketing'
import { calculateAge } from './dob.utils'
import {
  CampaignMessageStatus,
  ICampaignMessage,
  ICampaignRecipient,
  ICampaignRequest,
  ICampaignTriggerKeyword,
  ICampaignTriggerMessage,
} from '@dataTypes/marketing.type'
import { IContact } from '@dataTypes/contact.type'
import { IClinic } from '@dataTypes/clinic.type'
import moment from 'moment'
import { getFullName } from './fullname.util'
import { setTime, toUTC } from './time.util'
import { logger } from '@utils/safeLogger'

export const compareCampaignTriggerKeywordIds = (
  id1: string | number | undefined,
  id2: string | number | undefined,
): boolean => {
  if (id1 === undefined || id2 === undefined) {
    return id1 === id2
  }
  return String(id1) === String(id2)
}

export const calculatePercentageAndStatus = (
  current: number,
  previous: number,
): { percentage: number; status: StatusType } => {
  if (previous === 0 || current === 0) {
    return {
      percentage: 0,
      status: StatusType.neutral,
    }
  }

  const percentage = ((current - previous) / previous) * 100
  const roundedPercentage = Math.round(percentage * 10) / 10 // Round to 1 decimal place

  if (current > previous) {
    return { percentage: roundedPercentage, status: StatusType.increase }
  } else if (current < previous) {
    return {
      percentage: Math.abs(roundedPercentage),
      status: StatusType.decrease,
    }
  } else {
    return { percentage: 0, status: StatusType.neutral }
  }
}

export const shuffle = <T>(arr: T[]): T[] => {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

export const getStartOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0)
export const getEndOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59)

// Check if the day is valid for the month and correct the day if it's not
export const clampDayInMonth = (
  year: number,
  month: number,
  day: number,
): Date => {
  const last = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(day, last))
}

// Fetch the number value of the days of the week
export const parseRecurringDays = (daysString?: string): number[] => {
  if (!daysString) return []
  return daysString
    .split(',')
    .map((day) => day.trim())
    .filter(Boolean)
    .map((dayKey) => fullDaysOfWeek.findIndex((day) => day === dayKey))
    .filter((index): index is number => index !== undefined)
    .sort((a, b) => a - b)
}

// Get the nth weekday of the month
// Example input: 3rd Monday of the month
//         output: 2025-01-20
export const getNthWeekdayOfMonth = (
  year: number,
  month: number,
  weekday: number,
  nth: number,
): Date | null => {
  const firstOfMonth = new Date(year, month, 1)
  const firstDayOfWeek = firstOfMonth.getDay()
  const offsetDays = (weekday - firstDayOfWeek + 7) % 7
  const targetDay = 1 + offsetDays + (nth - 1) * 7
  const date = new Date(year, month, targetDay)
  return date.getMonth() === month ? date : null
}

// Get the nth occurrence of the weekday in the month
// Example input: 2025-01-20
//         output: 3
export const getNthOfWeekdayInMonth = (date: Date): number => {
  return Math.floor((date.getDate() - 1) / 7) + 1
}

// Check if two date ranges intersect
export const datesIntersect = (
  startA: Date,
  endA: Date,
  startB: Date,
  endB: Date,
): boolean => endA >= startB && startA <= endB

export const formatOrdinal = (num: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = num % 100
  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0])
}

export const getLocalDayKey = (date: Date): string =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

export const getSecondsFromTime = (time: string): number => {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60 + seconds
}

export const formatToHHmm = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number)
  const formattedTime = new Date()
  formattedTime.setHours(hours, minutes)
  return formattedTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export const replaceVariables = (rawHtml: string): string => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(rawHtml, 'text/html')

    const spans = doc.querySelectorAll('span.e-mention-chip')

    spans.forEach((span) => {
      const variableText = span.textContent?.trim()
      if (variableText) {
        // Find the matching variable in VARIABLE_LIST
        const variable = VARIABLE_LIST.find((v) => v.text === variableText)
        if (variable) {
          // Replace the span content with the sample value
          span.textContent = variable.sample
        }
      }
    })

    return doc.body.innerHTML
  } catch (error) {
    logger.error('Error processing preview content:', error)
    return rawHtml
  }
}

export const isAgeInRange = (range: string, birthday?: string): boolean => {
  if (birthday) {
    const [min, max] = range.split('-').map(Number)
    const age = calculateAge(birthday)
    return age >= min && age <= max
  }
  return false
}

export const analyzeRecipients = (
  newRecipients: ICampaignRecipient[],
  original?: ICampaignRecipient[],
  campaignId?: string,
): {
  deleted: string[]
  added: ICampaignRecipient[]
} => {
  const deleted =
    original?.filter(
      (item) =>
        !newRecipients.some(
          (newItem) =>
            newItem.campaign_recipient_id === item.campaign_recipient_id,
        ),
    ) ?? []

  const added = original
    ? newRecipients.filter(
        (item) =>
          !original.some(
            (originalItem) => originalItem.contact_id === item.contact_id,
          ),
      )
    : newRecipients
  return {
    deleted: deleted
      .map((item) => item.campaign_recipient_id)
      .filter((id): id is string => id !== undefined),
    added: campaignId
      ? added.map((item) => ({ ...item, campaign_id: campaignId }))
      : added,
  }
}

export const analyzeTimePoints = (
  campaignId: string,
  newTimePoints: ICampaignTriggerMessage[],
  original?: ICampaignTriggerMessage[],
): {
  deleted: string[]
  updated: ICampaignTriggerMessage[]
  added: ICampaignTriggerMessage[]
} => {
  const deleted =
    original?.filter(
      (item) =>
        !newTimePoints.some(
          (newItem) =>
            newItem.campaign_trigger_message_id ===
            item.campaign_trigger_message_id,
        ),
    ) ?? []
  const updated = original
    ? newTimePoints.filter((item) => {
        const originalItem = original.find(
          (o) =>
            o.campaign_trigger_message_id === item.campaign_trigger_message_id,
        )
        if (originalItem) {
          return (
            item.channel !== originalItem.channel ||
            item.send_on !== originalItem.send_on ||
            item.send_duration !== originalItem.send_duration ||
            item.text_content !== originalItem.text_content ||
            item.html_content !== originalItem.html_content
          )
        }
        return false
      })
    : newTimePoints
  const added = original
    ? newTimePoints.filter(
        (item) =>
          !original.some(
            (originalItem) =>
              originalItem.campaign_trigger_message_id ===
              item.campaign_trigger_message_id,
          ),
      )
    : newTimePoints
  return {
    deleted: deleted
      .map((item) => item.campaign_trigger_message_id)
      .filter((id): id is string => id !== undefined),
    updated,
    added: added.map((item) => ({
      ...item,
      campaign_trigger_message_id: undefined,
      campaign_id: campaignId,
    })),
  }
}

export const analyzeKeywords = (
  campaignId: string,
  newKeywords: ICampaignTriggerKeyword[],
  original?: ICampaignTriggerKeyword[],
): {
  deleted: string[]
  updated: ICampaignTriggerKeyword[]
  added: ICampaignTriggerKeyword[]
} => {
  const deleted =
    original?.filter(
      (item) =>
        !newKeywords.some(
          (newItem) =>
            newItem.campaign_trigger_keyword_id ===
            item.campaign_trigger_keyword_id,
        ),
    ) ?? []
  const updated = original
    ? newKeywords.filter((item) => {
        const originalItem = original.find(
          (o) =>
            o.campaign_trigger_keyword_id === item.campaign_trigger_keyword_id,
        )
        if (originalItem) {
          return (
            item.name !== originalItem.name ||
            item.keyword !== originalItem.keyword ||
            item.text_content !== originalItem.text_content ||
            item.html_content !== originalItem.html_content
          )
        }
        return false
      })
    : newKeywords
  const added = original
    ? newKeywords.filter(
        (item) =>
          !original.some(
            (originalItem) =>
              originalItem.campaign_trigger_keyword_id ===
              item.campaign_trigger_keyword_id,
          ),
      )
    : newKeywords
  return {
    deleted: deleted
      .map((item) => item.campaign_trigger_keyword_id)
      .filter((id): id is string => id !== undefined),
    updated,
    added: added.map((item) => ({
      ...item,
      campaign_trigger_keyword_id: undefined,
      campaign_id: campaignId,
    })),
  }
}

export const replaceLinksInText = (text: string, html: string): string => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const anchors = Array.from(doc.querySelectorAll('a'))

  let updatedText = text

  anchors.forEach((anchor) => {
    const anchorText = anchor.textContent?.trim() ?? ''
    const href = anchor.getAttribute('href') ?? ''

    if (anchorText && href) {
      updatedText = updatedText.replace(anchorText, href)
    }
  })

  return updatedText
}

export const buildCampaignVariableValues = (
  campaign: ICampaignRequest,
  contact: IContact,
  clinic?: IClinic | null,
  schedule_date_time?: Date,
): Record<string, string> => {
  const firstName =
    contact.first_name?.trim() || contact.patient?.first_name?.trim() || ''
  const lastName =
    contact.last_name?.trim() || contact.patient?.last_name?.trim() || ''
  const preferredName =
    contact.preferred_name?.trim() ||
    contact.patient?.preferred_name?.trim() ||
    firstName
  const fullName =
    getFullName(contact).trim() || `${firstName} ${lastName}`.trim()

  const apptDate = DateTime.fromJSDate(
    schedule_date_time ??
      (campaign.start_date ? new Date(campaign.start_date) : new Date()),
    { zone: 'utc' },
  ).setZone(campaign.timezone)

  const practice = clinic ?? campaign.provider?.clinic
  const providerName =
    campaign.provider?.first_name && campaign.provider?.last_name
      ? `${campaign.provider.first_name} ${campaign.provider.last_name}`
      : ''

  const promoDate =
    campaign.start_date && campaign.end_date
      ? `${moment(campaign.start_date).format('MMMM D,YYYY')} - ${moment(campaign.end_date).format('MMMM D,YYYY')}`
      : ''

  return {
    'Patient.FirstName': firstName,
    'Patient.LastName': lastName,
    'Patient.PreferredName': preferredName,
    'Patient.FullName': fullName,
    'Patient.Name': fullName,
    'Patient.Email': contact.email?.trim() || '',
    'Patient.Phone': contact.cellphone?.trim() || '',
    'Practice.Name': practice?.clinic_name?.trim() || '',
    'Practice.Phone': practice?.phone_number?.trim() || '',
    'Provider.Name': providerName,
    'Appointment.Date': moment(apptDate).format('MMMM D,YYYY'),

    appointment_date: moment(apptDate).format('MMMM D,YYYY'),
    patient_name: fullName,
    patient_number: contact.cellphone?.trim() || '',
    preferred_name: preferredName,
    practice_name: practice?.clinic_name?.trim() || '',
    practice_phone: practice?.phone_number?.trim() || '',
    practice_number: practice?.phone_number?.trim() || '',
    promo_date: promoDate,
    provider_name: providerName,
  }
}

export const applyCampaignVariables = (
  content: string,
  values: Record<string, string>,
): string => {
  if (!content) return content

  let result = content

  if (/e-mention-chip|<\w[\s\S]*>/.test(result)) {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(result, 'text/html')
      doc.querySelectorAll('span.e-mention-chip').forEach((span) => {
        const dataValue = span.getAttribute('data-value')?.trim()
        const textToken = span.textContent
          ?.trim()
          .replace(/^\{\{\s*|\s*\}\}$/g, '')
        const key = dataValue || textToken || ''
        const value = key ? values[key] : undefined
        if (value != null && value !== '') {
          span.replaceWith(doc.createTextNode(value))
        } else if (value === '') {
          // Clear known empty tokens so raw {{Patient.FirstName}} does not ship
          span.replaceWith(doc.createTextNode(''))
        }
      })
      result = doc.body.innerHTML
    } catch (error) {
      logger.error('Error applying campaign variable chips:', error)
    }
  }

  Object.entries(values).forEach(([key, value]) => {
    if (key.includes('.')) {
      result = result.replaceAll(`{{${key}}}`, value)
    } else {
      result = result.replaceAll(`(${key})`, value)
    }
  })

  return result
}

export const generateContent = (
  campaign: ICampaignRequest,
  contact: IContact,
  schedule_date_time?: Date,
  trigger_id?: string,
  clinic?: IClinic | null,
): ICampaignMessage => {
  const isEmail = campaign.channel === 'Email'
  const trigger = trigger_id
    ? campaign.campaign_trigger_message.find(
        (ctm) => ctm.campaign_trigger_message_id === trigger_id,
      )
    : undefined

  let content = trigger
    ? isEmail
      ? trigger.html_content || trigger.text_content || ''
      : (trigger.text_content as string)
    : isEmail
      ? campaign.html_content || campaign.text_content || ''
      : campaign.text_content!

  const html_content = trigger
    ? (trigger.html_content as string)
    : campaign.html_content!

  if (!isEmail) {
    content = replaceLinksInText(content, html_content)
  }

  const variableValues = buildCampaignVariableValues(
    campaign,
    contact,
    clinic,
    schedule_date_time,
  )
  content = applyCampaignVariables(content, variableValues)

  const subject = applyCampaignVariables(
    campaign.subject?.trim() || campaign.name,
    variableValues,
  )

  return {
    contact_id: contact.contact_id!,
    campaign_id: campaign.campaign_id!,
    classification: trigger_id ? 'Trigger' : 'Main',
    message_type: 'Outgoing',
    content,
    cellphone: isEmail ? null : contact.cellphone,
    email: isEmail ? (contact.email ?? null) : null,
    ...(isEmail ? { subject } : {}),
    status: CampaignMessageStatus.pending,
    resend_count: 0,
    schedule_date_time: schedule_date_time,
    is_hidden: false,
  }
}

export const generateCampaignMessages = (
  campaign: ICampaignRequest,
  clinic?: IClinic | null,
): ICampaignMessage[] => {
  const res: ICampaignMessage[] = []
  const isEmail = campaign.channel === 'Email'
  const recipients = campaign.campaign_recipient.filter((recipient) => {
    if (!recipient.contact) return false
    if (isEmail) return Boolean(recipient.contact.email?.trim())
    return Boolean(recipient.contact.cellphone)
  })

  if (
    campaign.sending_option === 'Immediately' &&
    campaign.type &&
    !['Reminder', 'Celebration'].includes(campaign.type)
  ) {
    recipients.forEach((recipient) => {
      res.push(
        generateContent(
          campaign,
          recipient.contact!,
          undefined,
          undefined,
          clinic,
        ),
      )
    })
  } else if (
    campaign.sending_option === 'Later' &&
    campaign.schedule_date &&
    campaign.schedule_time &&
    campaign.timezone
  ) {
    const _date = new Date(campaign.schedule_date)
    const [hours, minutes, seconds] = campaign.schedule_time
      .split(':')
      .map(Number)
    _date.setHours(hours, minutes, seconds, 0)
    const scheduleDate = toUTC(_date, campaign.timezone)

    recipients.forEach((recipient) => {
      res.push(
        generateContent(
          campaign,
          recipient.contact!,
          scheduleDate,
          undefined,
          clinic,
        ),
      )
    })
  }
  return res
}

export const generateTriggerDate = (
  _date: Date,
  sendOption: string,
  sendOn: number,
  sendDuration: string,
): Date => {
  const baseDate = DateTime.fromJSDate(_date)
  if (sendOption === 'After') {
    if (sendDuration === 'Hour/s')
      return baseDate.plus({ hours: sendOn }).toJSDate()
    else if (sendDuration === 'Day/s')
      return baseDate.plus({ days: sendOn }).toJSDate()
    else if (sendDuration === 'Week/s')
      return baseDate.plus({ weeks: sendOn }).toJSDate()
  } else if (sendOption === 'Before') {
    if (sendDuration === 'Hour/s')
      return baseDate.minus({ hours: sendOn }).toJSDate()
    else if (sendDuration === 'Day/s')
      return baseDate.minus({ days: sendOn }).toJSDate()
    else if (sendDuration === 'Week/s')
      return baseDate.minus({ weeks: sendOn }).toJSDate()
  }
  return baseDate.toJSDate()
}

const isUntilTomorrowUTC = (date: Date): boolean => {
  // date today in utc
  const today = Date.now()
  const tomorrow = today + 24 * 60 * 60 * 1000
  return date.getTime() >= today && date.getTime() <= tomorrow
}

export const generateTriggerMessages = (
  campaign: ICampaignRequest,
  clinic?: IClinic | null,
): ICampaignMessage[] => {
  const res: ICampaignMessage[] = []

  if (campaign.timezone) {
    const triggers = campaign.campaign_trigger_message.filter(
      (ctm) =>
        ctm.send_option &&
        ctm.send_on &&
        ctm.send_duration &&
        ctm.channel === campaign.channel &&
        (campaign.channel === 'Email'
          ? Boolean(ctm.html_content || ctm.text_content)
          : Boolean(ctm.text_content)) &&
        !ctm.is_hidden &&
        ctm.status === 'Active',
    )

    const eligibleRecipients = (campaign.campaign_recipient || []).filter(
      (recipient) => {
        if (!recipient.contact) return false
        if (campaign.channel === 'Email')
          return Boolean(recipient.contact.email?.trim())
        return Boolean(recipient.contact.cellphone)
      },
    )

    triggers.forEach((trigger) => {
      if (campaign.type === 'Reminder') {
        eligibleRecipients.forEach((recipient) => {
          if (recipient.contact?.patient?.appointments) {
            recipient.contact?.patient?.appointments.forEach((appointment) => {
              const apptDate = setTime(
                new Date(appointment.appt_date),
                appointment.appt_start,
              )
              const _scheduleDate = toUTC(apptDate, campaign.timezone!)

              const scheduleDate = generateTriggerDate(
                _scheduleDate,
                trigger.send_option,
                trigger.send_on,
                trigger.send_duration,
              )

              if (isUntilTomorrowUTC(scheduleDate)) {
                res.push(
                  generateContent(
                    campaign,
                    recipient.contact!,
                    scheduleDate,
                    trigger.campaign_trigger_message_id,
                    clinic,
                  ),
                )
              }
            })
          }
        })
        return res
      } else if (campaign.type === 'Celebration') {
        if (
          campaign.celebration_type_filter &&
          campaign.celebration_type_filter.length > 0
        ) {
          if (campaign.celebration_type_filter.includes('Birthday')) {
            eligibleRecipients.forEach((recipient) => {
              if (recipient.contact?.patient?.date_of_birth) {
                const birthday = new Date(
                  recipient.contact?.patient?.date_of_birth,
                )
                birthday.setFullYear(new Date().getFullYear())
                const _scheduleDate = toUTC(birthday, campaign.timezone!)
                const scheduleDate = generateTriggerDate(
                  _scheduleDate,
                  trigger.send_option,
                  trigger.send_on,
                  trigger.send_duration,
                )
                if (isUntilTomorrowUTC(scheduleDate)) {
                  res.push(
                    generateContent(
                      campaign,
                      recipient.contact!,
                      scheduleDate,
                      trigger.campaign_trigger_message_id,
                      clinic,
                    ),
                  )
                }
              }
            })
          }
          if (campaign.celebration_type_filter.includes('Anniversary')) {
            const anniversaryDate = new Date('January 17, 2024')
            anniversaryDate.setFullYear(new Date().getFullYear())
            const _scheduleDate = toUTC(anniversaryDate, campaign.timezone!)
            const scheduleDate = generateTriggerDate(
              _scheduleDate,
              trigger.send_option,
              trigger.send_on,
              trigger.send_duration,
            )
            if (isUntilTomorrowUTC(scheduleDate)) {
              eligibleRecipients.forEach((recipient) => {
                res.push(
                  generateContent(
                    campaign,
                    recipient.contact!,
                    scheduleDate,
                    trigger.campaign_trigger_message_id,
                    clinic,
                  ),
                )
              })
            }
          }
          if (campaign.celebration_type_filter.includes('Membership')) {
            eligibleRecipients.forEach((recipient) => {
              if (recipient.contact?.date_time_created_at) {
                const membershipDate = new Date(
                  recipient.contact?.date_time_created_at,
                )
                membershipDate.setFullYear(new Date().getFullYear())
                const _scheduleDate = toUTC(membershipDate, campaign.timezone!)
                const scheduleDate = generateTriggerDate(
                  _scheduleDate,
                  trigger.send_option,
                  trigger.send_on,
                  trigger.send_duration,
                )
                if (isUntilTomorrowUTC(scheduleDate)) {
                  res.push(
                    generateContent(
                      campaign,
                      recipient.contact!,
                      scheduleDate,
                      trigger.campaign_trigger_message_id,
                      clinic,
                    ),
                  )
                }
              }
            })
          }
        }
      } else if (
        campaign.sending_option === 'Immediately' &&
        campaign.start_date &&
        campaign.start_time
      ) {
        const _scheduleDate = toUTC(
          setTime(new Date(campaign.start_date), campaign.start_time),
          campaign.timezone!,
        )
        const scheduleDate = generateTriggerDate(
          _scheduleDate,
          trigger.send_option,
          trigger.send_on,
          trigger.send_duration,
        )
        if (isUntilTomorrowUTC(scheduleDate)) {
          eligibleRecipients.forEach((recipient) => {
            res.push(
              generateContent(
                campaign,
                recipient.contact!,
                scheduleDate,
                trigger.campaign_trigger_message_id,
                clinic,
              ),
            )
          })
        }
      } else if (
        campaign.sending_option === 'Later' &&
        campaign.schedule_date &&
        campaign.schedule_time
      ) {
        const _scheduleDate = toUTC(
          setTime(new Date(campaign.schedule_date), campaign.schedule_time),
          campaign.timezone!,
        )
        const scheduleDate = generateTriggerDate(
          _scheduleDate,
          trigger.send_option,
          trigger.send_on,
          trigger.send_duration,
        )
        if (isUntilTomorrowUTC(scheduleDate)) {
          eligibleRecipients.forEach((recipient) => {
            res.push(
              generateContent(
                campaign,
                recipient.contact!,
                scheduleDate,
                trigger.campaign_trigger_message_id,
                clinic,
              ),
            )
          })
        }
      } else if (
        campaign.sending_option === 'Recurring' &&
        campaign.start_date
      ) {
        if (
          campaign.recurring_interval === 'Daily' &&
          campaign.recurring_time &&
          campaign.recurring_duration
        ) {
          const startDate = setTime(
            new Date(campaign.start_date),
            campaign.recurring_time,
          )

          for (let day = 0; day <= campaign.recurring_duration; day++) {
            const currentDate = new Date(startDate)
            currentDate.setDate(startDate.getDate() + day)
            const _scheduleDate = toUTC(currentDate, campaign.timezone!)
            const scheduleDate = generateTriggerDate(
              _scheduleDate,
              trigger.send_option,
              trigger.send_on,
              trigger.send_duration,
            )
            if (isUntilTomorrowUTC(scheduleDate)) {
              eligibleRecipients.forEach((recipient) => {
                res.push(
                  generateContent(
                    campaign,
                    recipient.contact!,
                    scheduleDate,
                    trigger.campaign_trigger_message_id,
                    clinic,
                  ),
                )
              })
            }
          }
        } else if (
          campaign.recurring_interval === 'Weekly' &&
          campaign.recurring_days &&
          campaign.recurring_time &&
          campaign.recurring_duration
        ) {
          const startDate = setTime(
            new Date(campaign.start_date),
            campaign.recurring_time,
          )

          const dayNames = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ]
          const recurringDayNumbers = campaign.recurring_days
            .split(',')
            .map((day) => day.trim())
            .map((day) => dayNames.indexOf(day))
            .filter((dayNum) => dayNum !== -1)
            .sort((a, b) => a - b)

          for (let week = 0; week < campaign.recurring_duration; week++) {
            const weekStart = moment(startDate).add(week, 'weeks')

            for (const targetDayNumber of recurringDayNumbers) {
              const currentDayOfWeek = weekStart.day()
              const daysUntilTarget =
                (targetDayNumber - currentDayOfWeek + 7) % 7

              const targetDate = moment(weekStart).add(daysUntilTarget, 'days')
              const _scheduleDate = toUTC(
                targetDate.toDate(),
                campaign.timezone!,
              )
              const scheduleDate = generateTriggerDate(
                _scheduleDate,
                trigger.send_option,
                trigger.send_on,
                trigger.send_duration,
              )
              if (isUntilTomorrowUTC(scheduleDate)) {
                eligibleRecipients.forEach((recipient) => {
                  res.push(
                    generateContent(
                      campaign,
                      recipient.contact!,
                      scheduleDate,
                      trigger.campaign_trigger_message_id,
                      clinic,
                    ),
                  )
                })
              }
            }
          }
        }
      }
    })
  }
  return res
}
